import { NUTRITION_QUEUE_WINDOWS, type NutritionQueue } from "@nova-ai/shared"
import { Check, ChevronRight, CircleAlert, X } from "lucide-react"
import type { NutritionQueueSearch } from "../../services/use-nutrition-queue"
import { cn } from "../../lib/utils"
import { NUTRITION_STEPS, nutritionDuration } from "./nutrition-presentation"
import { NutritionHelp } from "./nutrition-help"

type Props = {
  data: NutritionQueue
  search: NutritionQueueSearch
  onChange: (search: NutritionQueueSearch) => void
}
export function NutritionPipeline({ data, search, onChange }: Props) {
  const totals = pipelineStages(data)
  const period = NUTRITION_QUEUE_WINDOWS[search.period ?? "7d"]
  return (
    <section
      aria-label="Nutrition pipeline"
      className="min-w-0 rounded-2xl border border-border bg-card shadow-card max-sm:rounded-lg"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-5 pt-4 max-sm:px-3.5 max-sm:pt-3.5">
        <h2 className="text-[14px] font-semibold">Pipeline · last {period.label}</h2>

        <div className="flex items-center gap-1">
          <p className="text-[12px] text-muted-foreground">
            Select a step to list the receipts sitting there now.
          </p>

          <NutritionHelp label="About pipeline counts">
            Counts cover retained nutrition receipts received during the selected period, not older
            backlog. One receipt can have several store/SKU scopes. Shared work can belong to
            multiple receipts. The first unresolved step owns each unfinished receipt. Non-actions
            keep their recorded outcome.
            {` ${data.metrics.unclassifiableRejected} rejected envelopes have unknown nutrition membership and are excluded.`}
          </NutritionHelp>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 p-3 max-md:grid-cols-2 max-sm:gap-1 max-sm:p-2">
        {NUTRITION_STEPS.map(({ key, title }, index) => {
          const stage = totals[index]
          if (!stage) {
            return null
          }
          const here = data.pipeline[key]
          return (
            <button
              key={key}
              type="button"
              aria-pressed={search.step === key}
              onClick={() =>
                onChange({ ...search, step: search.step === key ? undefined : key, view: "all" })
              }
              className="group/stage relative grid content-start gap-1 rounded-lg px-3.5 pt-3 pb-3.5 text-left outline-1 outline-transparent transition-[background,outline-color] duration-150 ease-[ease] hover:bg-foreground/4 focus-visible:outline-2 focus-visible:outline-ring aria-pressed:bg-muted aria-pressed:outline-foreground aria-pressed:focus-visible:outline-ring aria-pressed:hover:bg-foreground/4 max-sm:p-2.5"
            >
              <span className="flex items-start gap-2 text-[13px] font-medium leading-[1.35]">
                <span className="inline-flex size-5 flex-none items-center justify-center rounded-full bg-muted text-[11px] font-semibold group-aria-pressed/stage:bg-primary group-aria-pressed/stage:text-primary-foreground">
                  {index + 1}
                </span>

                {title}
              </span>

              <span className="mt-1.5 text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] tabular-nums max-sm:text-[22px]">
                {stage.total.toLocaleString()}
              </span>

              <span className="text-[12px] text-muted-foreground">{stage.unit}</span>

              <span className="mt-1.5 flex min-h-5 flex-wrap gap-x-2.5 gap-y-1">
                <PipelineIssues stage={stage} here={here} />
              </span>

              <span className="mt-0.5 text-[12px] text-muted-foreground">
                {here ? `${here.toLocaleString()} receipts here now` : "No receipts here now"}
              </span>

              {index < 3 && (
                <ChevronRight
                  aria-hidden="true"
                  className="absolute top-3.5 -right-[11px] size-3.5 text-muted-foreground max-md:hidden"
                />
              )}
            </button>
          )
        })}
      </div>
    </section>
  )
}

const OUTCOME_SEGMENTS = [
  { key: "done", label: "done", color: "bg-success" },
  { key: "in_progress", label: "in progress", color: "bg-info" },
  { key: "needs_attention", label: "need attention", color: "bg-warning" },
] as const
export function NutritionMetrics({ data, search, onChange }: Props) {
  const { metrics } = data
  const percentage = metrics.received
    ? Math.round((metrics.imagesReadyAndPublished / metrics.received) * 1000) / 10
    : null
  // Metrics and their legend use the whole cohort, unlike filtered list-tab counts.
  const groups = {
    in_progress: metrics.groups.inProgress,
    needs_attention: metrics.groups.needsAttention,
    done: metrics.groups.done,
  }
  const period = NUTRITION_QUEUE_WINDOWS[search.period ?? "7d"]
  return (
    <section
      aria-label="Nutrition metrics"
      className="grid grid-cols-3 gap-4 max-lg:gap-3 max-md:grid-cols-1"
    >
      <article className="min-w-0 rounded-2xl border border-border bg-card shadow-card max-sm:rounded-lg grid content-start gap-3 px-5 pt-4 pb-[18px] max-lg:p-4">
        <div className="grid gap-0.5">
          <h2 className="flex items-center justify-between text-[13px] font-medium">
            Images ready and published
            <NutritionHelp label="About image completion">
              Every applicable image and linked Pub/Sub publication must be complete. Pub/Sub
              acceptance does not confirm Magento processing. Resolved non-actions appear in Done
              but never count as image completion. The bar shows receipt outcomes for the whole
              period.
            </NutritionHelp>
          </h2>

          <p className="block text-[12px] text-muted-foreground">
            Images recorded and Pub/Sub accepted
          </p>
        </div>

        <div>
          <span className="block text-[28px] font-semibold leading-[1.1] tracking-[-0.02em] whitespace-nowrap tabular-nums">
            {percentage === null ? "—" : `${percentage}%`}
          </span>

          <span className="block text-[12px] text-muted-foreground">{`${metrics.imagesReadyAndPublished.toLocaleString()} of ${metrics.received.toLocaleString()} receipts`}</span>
        </div>

        <div
          className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={`${groups.done} done, ${groups.in_progress} in progress, ${groups.needs_attention} need attention`}
        >
          {OUTCOME_SEGMENTS.map(
            ({ key, color }) =>
              groups[key] > 0 && <span key={key} className={color} style={{ flex: groups[key] }} />
          )}
        </div>

        <div className="-mt-1 flex flex-wrap gap-x-3 gap-y-0">
          {OUTCOME_SEGMENTS.map(({ key, label, color }) => (
            <button
              type="button"
              key={key}
              className="inline-flex min-h-8 items-center gap-1.5 rounded-md text-[12px] whitespace-nowrap tabular-nums hover:underline hover:underline-offset-3 focus-visible:outline-2 focus-visible:outline-ring max-sm:min-h-11"
              onClick={() => onChange({ ...search, view: key })}
            >
              <span className={cn("size-2 flex-none rounded-xs", color)} />

              {`${groups[key].toLocaleString()} ${label}`}
            </button>
          ))}
        </div>
      </article>

      <article className="min-w-0 rounded-2xl border border-border bg-card shadow-card max-sm:rounded-lg grid content-start gap-3 px-5 pt-4 pb-[18px] max-lg:p-4">
        <div className="grid gap-0.5">
          <h2 className="flex items-center justify-between text-[13px] font-medium">
            Time to image in GCS
            <NutritionHelp label="About image timing">
              Receipt time to the last applicable recorded current image. Publication waiting is
              separate. {metrics.imageTiming.excluded} absent or invalid timings excluded. The plot
              shows up to 100 recent samples; metrics use every valid timing.
            </NutritionHelp>
          </h2>

          <p className="block text-[12px] text-muted-foreground">
            From receipt to the last store image
          </p>
        </div>

        <div className="flex flex-wrap gap-x-7 gap-y-2">
          <div>
            <span className="block text-[28px] font-semibold leading-[1.1] tracking-[-0.02em] whitespace-nowrap tabular-nums">
              {nutritionDuration(metrics.imageTiming.medianMs)}
            </span>

            <span className="block text-[12px] text-muted-foreground">median</span>
          </div>

          <div>
            <span className="block text-[20px] font-semibold leading-[1.1] tracking-[-0.02em] whitespace-nowrap tabular-nums">
              {nutritionDuration(metrics.imageTiming.p95Ms)}
            </span>

            <span className="block text-[12px] text-muted-foreground">95% finish within</span>
          </div>
        </div>

        <TimingPlot values={metrics.imageTiming.valuesMs} />

        <p className="block text-[12px] text-muted-foreground">{`${metrics.imageTiming.withinTarget} of ${metrics.imageTiming.samples} image-complete receipts met the 10-minute target`}</p>
      </article>

      <article className="min-w-0 rounded-2xl border border-border bg-card shadow-card max-sm:rounded-lg grid content-start gap-3 px-5 pt-4 pb-[18px] max-lg:p-4">
        <div className="grid gap-0.5">
          <h2 className="flex items-center justify-between text-[13px] font-medium">
            Images made current
            <NutritionHelp label="About image activity">
              Unique recorded transitions, measured by transition time rather than receipt time.
              Shared receipts count once. Repeated bytes can have distinct revisions. Failed and
              pending attempts are not image totals. Historical selection does not establish present
              freshness or available image bytes.
            </NutritionHelp>
          </h2>

          <p className="block text-[12px] text-muted-foreground">
            Recorded in GCS · last {period.label}
          </p>
        </div>

        <div>
          <span className="block text-[28px] font-semibold leading-[1.1] tracking-[-0.02em] whitespace-nowrap tabular-nums">
            {metrics.imagesMadeCurrent.toLocaleString()}
          </span>

          <span className="block text-[12px] text-muted-foreground">unique image transitions</span>
        </div>

        <ActivityPlot activity={data.activity} period={search.period ?? "7d"} />

        <div className="-mt-1 flex flex-wrap gap-x-3 gap-y-0">
          <span className="inline-flex min-h-8 items-center gap-1.5 rounded-md text-[12px] whitespace-nowrap tabular-nums">
            <span className="size-2 flex-none rounded-xs bg-success" />
            Made current
          </span>
        </div>
      </article>
    </section>
  )
}
function TimingPlot({ values }: { values: number[] }) {
  const x = (ms: number) => 8 + Math.min(ms / 900000, 1) * 304
  return (
    <div>
      <svg
        className="block h-auto w-full"
        viewBox="0 0 320 44"
        role="img"
        aria-label={`Image timing sample, ${values.length} plotted receipts. Target ten minutes; warning rings exceed it.`}
      >
        <path
          d="M8 38H312 M8 35V41 M109.33 35V41 M210.67 35V41 M312 35V41"
          className="stroke-muted-foreground/45"
        />

        <path
          d="M210.67 4V38"
          className="stroke-foreground"
          strokeWidth="1.5"
          strokeDasharray="3 3"
        />

        {values.map((ms, index) => (
          <circle
            key={index}
            cx={x(ms)}
            cy={22 + (((index * 7) % 5) - 2) * 4}
            r={ms <= 600000 ? 4 : 3.5}
            className={ms <= 600000 ? "fill-success/75" : "fill-none stroke-warning"}
            strokeWidth="2"
          >
            <title>
              {nutritionDuration(ms)}

              {ms > 600000 ? " · over target" : ""}
            </title>
          </circle>
        ))}
      </svg>

      <div className="-mt-1.5 grid grid-cols-3 text-[11px] text-muted-foreground">
        <span>0</span>

        <span>5 min</span>

        <span className="flex justify-between gap-2">
          10 · target <span>15+</span>
        </span>
      </div>
    </div>
  )
}
function ActivityPlot({
  activity,
  period,
}: {
  activity: NutritionQueue["activity"]
  period: "24h" | "7d"
}) {
  const maximum = Math.max(1, ...activity.map((bucket) => bucket.imagesMadeCurrent))
  const width = 240 / Math.max(1, activity.length)
  return (
    <div>
      <svg
        className="block h-14 w-full"
        viewBox="0 0 240 56"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${period === "7d" ? "Daily" : "Hourly"} image transitions, oldest to newest`}
      >
        {activity.map((bucket, index) => (
          <g key={bucket.from}>
            <title>{`${bucket.from} – ${bucket.to}: ${bucket.imagesMadeCurrent} image transitions`}</title>

            <rect x={index * width} y="0" width={width} height="56" fill="transparent" />

            {bucket.imagesMadeCurrent > 0 && (
              <rect
                x={index * width + width * 0.15}
                y={56 - (bucket.imagesMadeCurrent / maximum) * 52}
                width={width * 0.7}
                height={(bucket.imagesMadeCurrent / maximum) * 52}
                rx="1"
                className="fill-success"
              />
            )}
          </g>
        ))}
      </svg>

      <div className="-mt-1.5 flex justify-between text-[11px] text-muted-foreground">
        <span>{period === "7d" ? "7 days ago" : "24 h ago"}</span>

        <span>{period === "7d" ? "3.5 days" : "12 h"}</span>

        <span>Now</span>
      </div>
    </div>
  )
}
function pipelineStages(data: NutritionQueue) {
  const t = data.pipelineTotals
  return [
    {
      total: t.acceptedReceipts,
      unit: "nutrition receipts accepted",
      issues: t.rejectedReceipts,
      issueLabel: "rejected",
      failure: true,
    },
    {
      total: t.actualScopes,
      unit: "retained receipt scopes",
      issues: t.generationPendingScopes,
      issueLabel: "waiting",
      failure: false,
    },
    {
      total: t.currentScopes,
      unit: "receipt scopes with images",
      issues: t.generationFailedScopes,
      issueLabel: "failed",
      failure: true,
    },
    {
      total: t.publicationAcceptedScopes,
      unit: "receipt scopes published",
      issues: t.publicationWaitingScopes,
      issueLabel: "waiting",
      failure: false,
    },
  ]
}

function PipelineIssues({
  stage,
  here,
}: {
  stage: ReturnType<typeof pipelineStages>[number]
  here: number
}) {
  if (stage.issues > 0) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-[12px] font-medium tabular-nums",
          stage.failure ? "text-destructive" : "text-info"
        )}
      >
        {stage.failure ? (
          <X className="size-3" />
        ) : (
          <span className="size-1.5 rounded-full bg-current" />
        )}

        {`${stage.issues.toLocaleString()} ${stage.issueLabel}`}
      </span>
    )
  }
  if (here > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[12px] font-medium tabular-nums text-warning">
        <CircleAlert className="size-3" />
        Unresolved
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-[12px] font-medium tabular-nums text-success">
      <Check className="size-3" />
      Nothing waiting
    </span>
  )
}
